import { LoggerService } from '../shared/services/logger.service';
import { inject, singleton } from 'tsyringe';
import {
  AppEntryPointService,
  IAppEntryPointDelegate,
} from './services/app-entry-point.service';
import { OWExtensionWrapper } from './ow-odk-wrappers/ow-extension-wrapper';
import {
  IPromotionWindowManagerService,
  kNameOfPromotionWindowManagerService,
} from './interfaces/promotion-window-manager-service.interface';
import { OWApplicationWrapper } from './ow-odk-wrappers/ow-application-wrapper';
import {
  ContentRecommendationService,
  IContentRecommendationServiceDelegate,
} from './services/promotion-recommendation-service/content-recommendation.service';
import { Game } from './models/game.model';
import { Promotion } from './models/promotion.model';
import { TesterWindow } from './windows/tester-window';
import { TimerService } from '../shared/services/timer.service';
import {
  PromotionAnalyticsService,
  kNameOfPromotionAnalyticsService,
} from './services/promotion-analytics/promotion-analytics.service';
import { ContentRecommendationUserAction } from '../shared/constants';
import { OWGamesWrapper } from './ow-odk-wrappers/ow-games-wrapper';
import { NoRecommendationReasons } from './services/promotion-recommendation-service/no-recommendations-reasons.enums';

// Allow final logs/localStorage to be flushed
const kShutdownTimeoutForFlushingLocalStorageInMs = 100;

// -----------------------------------------------------------------------------
@singleton()
export class BackgroundController
  implements IAppEntryPointDelegate, IContentRecommendationServiceDelegate
{
  private readonly logger = LoggerService.getCategory('BackgroundController');
  private runningGameId: number;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly entryPointService: AppEntryPointService,
    private readonly owExtensionWrapper: OWExtensionWrapper,
    private readonly contentRecommendationService: ContentRecommendationService,
    @inject(kNameOfPromotionWindowManagerService)
    private readonly promotionWinManager: IPromotionWindowManagerService,
    private readonly owApplicationWrapper: OWApplicationWrapper,
    private readonly testerWindow: TesterWindow,
    private readonly shutdownTimer: TimerService,
    @inject(kNameOfPromotionAnalyticsService)
    private readonly promotionAnalyticsService: PromotionAnalyticsService,
    private readonly gamesWrapper: OWGamesWrapper,
  ) {
    this.entryPointService.addListener(this);
    this.promotionWinManager.addListener(this);
    this.contentRecommendationService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // NOTE: Because the BackgroundController implements the AppStateController
  // interface, we cannot instantiate any states (e.g. AppStateInit) from the
  // ctor of BackgroundController.
  public async run() {
    try {
      await this.entryPointService.initialize();
      await this.printStartupInformationToConsole();
    } catch (ex) {
      this.logger.error('Error:', ex);
    }
  }

  // ---------------------------------------------------------------------------
  private async printStartupInformationToConsole() {
    await this.owExtensionWrapper.initialize();
    const { manifest } = this.owExtensionWrapper;
    this.logger.info(`App version: ${manifest?.meta?.version}`);
  }

  // ---------------------------------------------------------------------------
  private async shutdown() {
    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info('We have open windows - not closing app');
      return;
    }

    if (this.runningGameId) {
      this.logger.info(
        `We have running game, ${this.runningGameId}, Not shutting down`,
      );
      return;
    }

    this.logger.info('Shutting down ...');
    this.closeBackgroundWithDelay();
  }

  // ---------------------------------------------------------------------------
  private async hasOpenWindows(): Promise<boolean> {
    const isOpen = await this.promotionWinManager.isPromotionShown();
    if (isOpen) {
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  private async closeBackgroundWithDelay() {
    await this.shutdownTimer.sleep(kShutdownTimeoutForFlushingLocalStorageInMs);
    this.owApplicationWrapper.shutdown();
  }

  // ---------------------------------------------------------------------------
  // AppEntryPointDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onLaunchedWithDock() {
    await this.testerWindow.show();
  }

  // ---------------------------------------------------------------------------
  async onLaunchedWithGame(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game started ${gameId}, Ignoring`);
      this.shutdown();
      return;
    }

    this.contentRecommendationService.gameStarted(gameId);
    this.runningGameId = gameId;
  }

  // ---------------------------------------------------------------------------
  private async shouldGameBeExcluded(gameId: number) {
    try {
      const game = await this.gamesWrapper.getGameDBInfo(gameId);

      if (
        game?.gameInfo?.TypeString === 'Application' ||
        game?.installedGameInfo?.GameInfo?.TypeString === 'Application'
      ) {
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // IContentRecommendationServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onNoRecommendations(reason: NoRecommendationReasons) {
    if (reason === NoRecommendationReasons.ConfiguredForEnd) {
      return;
    }

    this.runningGameId = null;
    this.shutdown();
  }

  // ---------------------------------------------------------------------------
  async onShowPromotion(game: Game, promotion: Promotion) {
    this.promotionWinManager.show(game.id, promotion.id, promotion.type, null);
  }

  // ---------------------------------------------------------------------------
  // OWGameListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onGameStarted(gameId: number) {
    const hasOpenWindows = await this.hasOpenWindows();
    if (hasOpenWindows) {
      this.logger.info(
        'Game started while promotion window is still open,' +
          ' closing promotion window',
      );

      const { gameId, promotionId, promotionType } =
        this.promotionWinManager?.getPromotionInfo();

      this.promotionAnalyticsService.reportContentRecommend(
        gameId,
        promotionId,
        promotionType,
        ContentRecommendationUserAction.IgnoredByLaunchingGame,
      );
      this.promotionWinManager.close();
    }

    if (this.runningGameId) {
      return;
    }

    this.contentRecommendationService.gameStarted(gameId);
  }

  // ---------------------------------------------------------------------------
  async onGameEnded(gameId: number) {
    const shouldExcludeGame = await this.shouldGameBeExcluded(gameId);
    if (shouldExcludeGame) {
      this.logger.info(`Excluded Game Ended ${gameId}, Ignoring`);
      return;
    }

    console.log('onGameEnded', gameId);
    this.contentRecommendationService.gameEnded(gameId);
    this.runningGameId = gameId;
  }

  // ---------------------------------------------------------------------------
  // IPromotionsWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async onPromotionWindowClosed() {
    this.logger.info('Promotion Window closed, Shutting down in 3 seconds');
    setTimeout(() => {
      this.runningGameId = null;
      this.shutdown();
    }, 3000);
  }
}
